// Multi-family domain schemas. Only meaningful when site_mode='multi_family';
// commercial/single-tenant installs leave these tables empty.
import { z } from 'zod';
import { UUIDSchema } from '../ids.js';
export const ResidentRelationship = z.enum(['leaseholder', 'occupant', 'minor', 'caregiver']);
// ---------- Building ----------
export const Building = z.object({
    id: UUIDSchema,
    tenant_id: UUIDSchema,
    name: z.string(),
    address: z.string().nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const CreateBuildingBody = z.object({
    name: z.string().min(1).max(120),
    address: z.string().max(500).nullable().optional(),
});
export const UpdateBuildingBody = z.object({
    name: z.string().min(1).max(120).optional(),
    address: z.string().max(500).nullable().optional(),
});
// ---------- Unit ----------
export const Unit = z.object({
    id: UUIDSchema,
    tenant_id: UUIDSchema,
    building_id: UUIDSchema,
    unit_number: z.string(),
    floor: z.string().nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const CreateUnitBody = z.object({
    building_id: UUIDSchema,
    unit_number: z.string().min(1).max(40),
    floor: z.string().max(40).nullable().optional(),
});
export const UpdateUnitBody = z.object({
    unit_number: z.string().min(1).max(40).optional(),
    floor: z.string().max(40).nullable().optional(),
});
// ---------- Household ----------
export const Household = z.object({
    id: UUIDSchema,
    tenant_id: UUIDSchema,
    unit_id: UUIDSchema,
    label: z.string().nullable(),
    moved_in_at: z.string().datetime(),
    moved_out_at: z.string().datetime().nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const CreateHouseholdBody = z.object({
    unit_id: UUIDSchema,
    label: z.string().max(120).nullable().optional(),
    moved_in_at: z.string().datetime().optional(),
});
export const UpdateHouseholdBody = z.object({
    label: z.string().max(120).nullable().optional(),
    moved_in_at: z.string().datetime().optional(),
    moved_out_at: z.string().datetime().nullable().optional(),
});
// ---------- ResidentProfile ----------
export const ResidentProfile = z.object({
    id: UUIDSchema,
    tenant_id: UUIDSchema,
    household_id: UUIDSchema,
    name: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    relationship: ResidentRelationship,
    user_id: UUIDSchema.nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const CreateResidentBody = z.object({
    household_id: UUIDSchema,
    name: z.string().min(1).max(120),
    email: z.string().email().max(254).nullable().optional(),
    phone: z.string().max(40).nullable().optional(),
    relationship: ResidentRelationship.default('occupant'),
    /** When set, an invite is emailed and a portal User account is wired up. */
    invite: z.boolean().default(false),
});
export const UpdateResidentBody = z.object({
    name: z.string().min(1).max(120).optional(),
    email: z.string().email().max(254).nullable().optional(),
    phone: z.string().max(40).nullable().optional(),
    relationship: ResidentRelationship.optional(),
});
// ---------- Bulk move-in ----------
//
// Operators turn a property over a few dozen units at a time. Doing this
// row by row in the UI is slow and easy to get wrong (which household
// label goes with which unit, who's a leaseholder vs. occupant). This
// schema accepts an array of unit-keyed groups; each group is the unit +
// optional household label + the residents to onboard into it.
export const BulkMoveInResident = z.object({
    name: z.string().min(1).max(120),
    email: z.string().email().max(254).nullable().optional(),
    phone: z.string().max(40).nullable().optional(),
    relationship: ResidentRelationship.default('occupant'),
    invite: z.boolean().default(false),
});
export const BulkMoveInRow = z.object({
    /** Unit number, e.g. "4B" or "204". Required. */
    unit_number: z.string().min(1).max(40),
    /**
     * Building name — only required when the property has more than one
     * building and `unit_number` is ambiguous. When omitted the server
     * resolves the unique unit by unit_number alone and 422s if multiple
     * match.
     */
    building_name: z.string().max(120).nullable().optional(),
    household_label: z.string().max(120).nullable().optional(),
    moved_in_at: z.string().datetime().nullable().optional(),
    residents: z.array(BulkMoveInResident).min(1).max(8),
});
export const BulkMoveInBody = z.object({
    rows: z.array(BulkMoveInRow).min(1).max(200),
});
export const BulkMoveInRowResult = z.object({
    row_index: z.number().int().nonnegative(),
    status: z.enum(['created', 'would_create', 'error']),
    household_id: UUIDSchema.nullable(),
    resident_ids: z.array(UUIDSchema),
    invite_count: z.number().int().nonnegative(),
    errors: z.array(z.string()),
});
export const BulkMoveInResponse = z.object({
    dry_run: z.boolean(),
    summary: z.object({
        rows_total: z.number().int().nonnegative(),
        rows_ok: z.number().int().nonnegative(),
        rows_error: z.number().int().nonnegative(),
        households: z.number().int().nonnegative(),
        residents: z.number().int().nonnegative(),
        invites: z.number().int().nonnegative(),
    }),
    rows: z.array(BulkMoveInRowResult),
});
// ---------- Amenity ----------
export const Amenity = z.object({
    id: UUIDSchema,
    tenant_id: UUIDSchema,
    building_id: UUIDSchema.nullable(),
    name: z.string(),
    description: z.string().nullable(),
    schedule_id: UUIDSchema.nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const CreateAmenityBody = z.object({
    name: z.string().min(1).max(120),
    description: z.string().max(2000).nullable().optional(),
    building_id: UUIDSchema.nullable().optional(),
    schedule_id: UUIDSchema.nullable().optional(),
});
export const UpdateAmenityBody = z.object({
    name: z.string().min(1).max(120).optional(),
    description: z.string().max(2000).nullable().optional(),
    building_id: UUIDSchema.nullable().optional(),
    schedule_id: UUIDSchema.nullable().optional(),
});
// ---------- GuestPass ----------
export const GuestPass = z.object({
    id: UUIDSchema,
    tenant_id: UUIDSchema,
    sponsor_profile_id: UUIDSchema,
    guest_name: z.string(),
    guest_email: z.string().nullable(),
    valid_from: z.string().datetime(),
    valid_until: z.string().datetime(),
    recurring_rule: z.unknown().nullable().optional(),
    restrictions: z.unknown().nullable().optional(),
    credential_id: UUIDSchema.nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const CreateGuestPassBody = z
    .object({
    sponsor_profile_id: UUIDSchema,
    guest_name: z.string().min(1).max(120),
    guest_email: z.string().email().max(254).nullable().optional(),
    valid_from: z.string().datetime(),
    valid_until: z.string().datetime(),
    recurring_rule: z.unknown().nullable().optional(),
    restrictions: z.unknown().nullable().optional(),
})
    .refine((b) => new Date(b.valid_until) > new Date(b.valid_from), {
    message: 'valid_until must be strictly after valid_from',
    path: ['valid_until'],
});
// ---------- CallboxDirectoryEntry ----------
export const CallboxVisibility = z.enum(['public', 'private']);
export const CallboxDirectoryEntry = z.object({
    id: UUIDSchema,
    tenant_id: UUIDSchema,
    household_id: UUIDSchema,
    display_name: z.string(),
    phone: z.string().nullable(),
    visibility: CallboxVisibility,
    call_routing_json: z.unknown().nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const CreateCallboxEntryBody = z.object({
    household_id: UUIDSchema,
    display_name: z.string().min(1).max(120),
    phone: z.string().max(40).nullable().optional(),
    visibility: CallboxVisibility.default('public'),
    call_routing_json: z.unknown().nullable().optional(),
});
export const UpdateCallboxEntryBody = z.object({
    display_name: z.string().min(1).max(120).optional(),
    phone: z.string().max(40).nullable().optional(),
    visibility: CallboxVisibility.optional(),
    call_routing_json: z.unknown().nullable().optional(),
});
//# sourceMappingURL=multifamily.js.map